import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PenLine, Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { borrarFirma, getFirma, getFirmaImagen, subirFirma } from "@/lib/api";

export function ConfiguracionPage() {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [msg, setMsg] = useState<{ tipo: "ok" | "err"; texto: string } | null>(null);

  const firmaQ = useQuery({ queryKey: ["firma"], queryFn: getFirma });
  const imagenQ = useQuery({
    queryKey: ["firma-imagen"],
    queryFn: getFirmaImagen,
    enabled: firmaQ.data?.tiene_firma === true,
  });

  // ObjectURL de la firma guardada (se libera al desmontar/cambiar).
  const [guardadaUrl, setGuardadaUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!imagenQ.data) {
      setGuardadaUrl(null);
      return;
    }
    const url = URL.createObjectURL(imagenQ.data);
    setGuardadaUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [imagenQ.data]);

  const subirM = useMutation({
    mutationFn: (f: File) => subirFirma(f),
    onSuccess: () => {
      setMsg({ tipo: "ok", texto: "Firma guardada. Se usará en tus formatos autorizados." });
      setArchivo(null);
      setPreview(null);
      if (fileRef.current) fileRef.current.value = "";
      qc.invalidateQueries({ queryKey: ["firma"] });
      qc.invalidateQueries({ queryKey: ["firma-imagen"] });
    },
    onError: (e) => setMsg({ tipo: "err", texto: (e as Error).message }),
  });

  const borrarM = useMutation({
    mutationFn: borrarFirma,
    onSuccess: () => {
      setMsg({ tipo: "ok", texto: "Firma eliminada." });
      qc.invalidateQueries({ queryKey: ["firma"] });
      qc.invalidateQueries({ queryKey: ["firma-imagen"] });
    },
    onError: (e) => setMsg({ tipo: "err", texto: (e as Error).message }),
  });

  const onFile = (f: File | null) => {
    setMsg(null);
    setArchivo(f);
    setPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return f ? URL.createObjectURL(f) : null;
    });
  };

  return (
    <div className="max-w-2xl space-y-4">
      <div className="bg-white border rounded-lg p-5">
        <div className="flex items-center gap-2 mb-1">
          <PenLine className="w-4 h-4 text-slate-500" />
          <h2 className="text-base font-semibold text-slate-800">Mi firma</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Sube una imagen de tu firma manuscrita (PNG o JPG, máx. 1 MB, de
          preferencia con fondo blanco o transparente). Se estampará en los
          formatos de vacaciones —tuya en <b>Firma de Conformidad</b> y la de tu
          equipo en <b>Firma de Autorización</b>— cuando la solicitud ya esté{" "}
          <b>autorizada</b>.
        </p>

        {firmaQ.data?.tiene_firma && guardadaUrl && !preview && (
          <div className="mb-4">
            <div className="text-xs font-medium text-slate-600 mb-1.5">Firma guardada:</div>
            <div className="inline-block border rounded p-3 bg-slate-50">
              <img src={guardadaUrl} alt="Firma guardada" className="max-h-24 max-w-xs" />
            </div>
            {firmaQ.data.actualizado_en && (
              <div className="text-[11px] text-slate-400 mt-1">
                Actualizada: {firmaQ.data.actualizado_en.slice(0, 16).replace("T", " ")}
              </div>
            )}
          </div>
        )}
        {firmaQ.data && !firmaQ.data.tiene_firma && !preview && (
          <div className="mb-4 text-sm text-slate-500 border border-dashed rounded p-4 text-center">
            Aún no tienes una firma guardada.
          </div>
        )}
        {preview && (
          <div className="mb-4">
            <div className="text-xs font-medium text-slate-600 mb-1.5">Vista previa (sin guardar):</div>
            <div className="inline-block border rounded p-3 bg-slate-50">
              <img src={preview} alt="Vista previa de la firma" className="max-h-24 max-w-xs" />
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={(e) => onFile(e.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            className="border rounded px-3 py-1.5 text-sm hover:bg-slate-50"
            onClick={() => fileRef.current?.click()}
          >
            Elegir imagen…
          </button>
          <button
            type="button"
            disabled={!archivo || subirM.isPending}
            onClick={() => archivo && subirM.mutate(archivo)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <Upload className="w-3.5 h-3.5" />
            {subirM.isPending ? "Guardando…" : "Guardar firma"}
          </button>
          {firmaQ.data?.tiene_firma && (
            <button
              type="button"
              disabled={borrarM.isPending}
              onClick={() => {
                if (confirm("¿Eliminar tu firma guardada?")) borrarM.mutate();
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Eliminar
            </button>
          )}
        </div>

        {msg && (
          <div
            className={`mt-3 text-sm rounded px-3 py-2 border ${
              msg.tipo === "ok"
                ? "bg-green-50 border-green-200 text-green-700"
                : "bg-red-50 border-red-200 text-red-700"
            }`}
          >
            {msg.texto}
          </div>
        )}
      </div>
    </div>
  );
}
